// @core/stores/fiscalization/FiscalizationFiscalProviderStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { fiscalizationApi } from '@core/api';
import type {
  CreateFiscalProviderRequest,
  FiscalProvider,
  TestFiscalProviderRequest,
  TestFiscalProviderResponse,
  UpdateFiscalProviderRequest,
} from '@core/types/fiscalization';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class FiscalizationFiscalProviderStore {
  // List state
  items: FiscalProvider[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: FiscalProvider | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, FiscalProvider>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): FiscalProvider | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: FiscalProvider | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create fiscal provider
   */
  async CreateFiscalProvider(data: CreateFiscalProviderRequest): Promise<FiscalProvider> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.CreateFiscalProvider(data);
      const item = response.fiscal_provider;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete fiscal provider
   */
  async DeleteFiscalProvider(id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await fiscalizationApi.DeleteFiscalProvider(id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === id) {
          this.currentItem = null;
        }

        this.removeFromCache(id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get fiscal provider by ID
   */
  async GetFiscalProvider(id: string): Promise<FiscalProvider> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.GetFiscalProvider(id);
      const item = response.fiscal_provider;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List fiscal providers
   */
  async ListFiscalProviders(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.ListFiscalProviders(params);

      runInAction(() => {
        this.items = response.fiscal_providers?.providers ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Test fiscal provider connection
   */
  async TestFiscalProvider(id: string, data: TestFiscalProviderRequest): Promise<TestFiscalProviderResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await fiscalizationApi.TestFiscalProvider(id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update fiscal provider
   */
  async UpdateFiscalProvider(id: string, data: UpdateFiscalProviderRequest): Promise<FiscalProvider> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await fiscalizationApi.UpdateFiscalProvider(id, data);
      const item = response.fiscal_provider;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const fiscalizationFiscalProviderStore = new FiscalizationFiscalProviderStore();
